import { regions, years, genreOptions, sortOptions } from "@/mocks/movies";
import type { MovieType } from "@/mocks/movies";

export interface CategoryFilters {
  region: string;
  year: string;
  genre: string;
  sort: string;
}

interface FilterPanelProps {
  type: MovieType;
  filters: CategoryFilters;
  onChange: (key: keyof CategoryFilters, value: string) => void;
  onReset: () => void;
}

function FilterGroup({
  title,
  icon,
  options,
  active,
  onSelect,
}: {
  title: string;
  icon: string;
  options: string[];
  active: string;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="py-4 border-b border-background-200/60 last:border-b-0">
      <div className="flex items-center gap-2 mb-3">
        <i className={`${icon} text-primary-400 text-base`} />
        <h3 className="text-sm font-semibold text-foreground-900">{title}</h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onSelect(option)}
            className={`px-3 py-1 rounded-md text-xs transition-colors whitespace-nowrap cursor-pointer ${
              active === option
                ? "bg-primary-500 text-background-50"
                : "bg-background-200/70 text-foreground-700 hover:bg-background-300/70"
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterPanel({
  type,
  filters,
  onChange,
  onReset,
}: FilterPanelProps) {
  const hasActive =
    filters.region !== "全部" ||
    filters.year !== "全部" ||
    filters.genre !== "全部";

  return (
    <div className="w-full lg:w-64 shrink-0 rounded-lg bg-background-100 border border-background-200/70 p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <i className="ri-filter-3-line text-lg text-foreground-900" />
          <h2 className="font-heading text-base font-bold text-foreground-950">
            筛选条件
          </h2>
        </div>
        {hasActive && (
          <button
            type="button"
            onClick={onReset}
            className="text-xs text-accent-400 hover:text-accent-300 transition-colors cursor-pointer whitespace-nowrap"
          >
            重置筛选
          </button>
        )}
      </div>

      <FilterGroup
        title="地区"
        icon="ri-earth-line"
        options={regions}
        active={filters.region}
        onSelect={(value) => onChange("region", value)}
      />
      <FilterGroup
        title="年份"
        icon="ri-calendar-line"
        options={years}
        active={filters.year}
        onSelect={(value) => onChange("year", value)}
      />
      <FilterGroup
        title="类型"
        icon="ri-price-tag-3-line"
        options={genreOptions[type]}
        active={filters.genre}
        onSelect={(value) => onChange("genre", value)}
      />
      <FilterGroup
        title="排序"
        icon="ri-sort-desc"
        options={sortOptions.map((item) => item.label)}
        active={
          sortOptions.find((item) => item.key === filters.sort)?.label || "最近更新"
        }
        onSelect={(value) => {
          const target = sortOptions.find((item) => item.label === value);
          onChange("sort", target ? target.key : "latest");
        }}
      />
    </div>
  );
}